import type { CSSProperties } from 'react';
import { Switch } from 'react-aria-components';
import { Image as ImageIcon, Lock } from 'lucide-react';

export interface ImageToggleProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  /** When true, the toggle is visible but non-interactive with an upgrade tooltip. */
  disabled: boolean;
  /** Displayed as the title-tooltip label when `disabled`. */
  upgradeHint?: string;
}

const wrapperStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 10px',
  borderRadius: 'var(--radius-full)',
  border: '1px solid color-mix(in srgb, var(--color-border) 74%, transparent)',
  background: 'color-mix(in srgb, var(--color-surface-solid) 58%, transparent)',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--type-meta-size)',
  fontWeight: 'var(--weight-medium)',
  color: 'var(--color-text-secondary)',
  cursor: 'default',
};

export function ImageToggle({ enabled, onChange, disabled, upgradeHint }: ImageToggleProps) {
  return (
    <div
      style={{
        ...wrapperStyle,
        opacity: disabled ? 0.62 : 1,
      }}
      title={disabled ? upgradeHint : undefined}
      aria-disabled={disabled || undefined}
    >
      <ImageIcon size={14} strokeWidth={2} />
      <span>Include images</span>
      {disabled && <Lock size={12} strokeWidth={2} aria-hidden />}
      <Switch
        isSelected={enabled}
        isDisabled={disabled}
        onChange={onChange}
        aria-label="Include images in lesson"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        {({ isSelected }) => (
          <span
            style={{
              position: 'relative',
              display: 'inline-block',
              width: 28,
              height: 16,
              borderRadius: 999,
              background: isSelected ? 'var(--color-accent)' : 'var(--color-surface-border)',
              transition: 'background var(--motion-fast) var(--motion-ease-out)',
            }}
          >
            <span
              style={{
                position: 'absolute',
                top: 2,
                left: isSelected ? 14 : 2,
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: 'var(--color-surface)',
                transition: 'left var(--motion-fast) var(--motion-ease-out)',
              }}
            />
          </span>
        )}
      </Switch>
    </div>
  );
}
